import { useState } from 'react';

// A provider's mark: its vendor logo where one is shipped, otherwise a coloured
// badge with its initials.
//
// The logo is opt-in (`logo`) rather than automatic because it needs room. At the
// 18px of an activity row or the 26px of a recent-items list a vendor mark is an
// unreadable smudge, while two initials still read — so the small call sites stay
// as badges and the large ones (the provider list, the provider header) ask for the
// logo.
//
// Assets live in `public/assets/<providerId>.svg`, keyed by provider id: that is
// what makes this work for a second vendor without touching the component. A
// provider with no asset falls back to initials rather than showing a broken
// image, which is also what happens if the file is renamed or fails to load.

// How much taller a logo is drawn than the badge it replaces. `size` is the box a
// letter badge fills edge to edge; a logo asset has its own margins and, being
// wider than tall, gets fitted by its width — so at the same number it reads
// noticeably smaller. This brings the ink back to the weight the layout intended.
const LOGO_SCALE = 1.5;

export default function ProviderAvatar({ provider, size = 32, logo = false }) {
  const [broken, setBroken] = useState(false);

  const label = provider?.shortName || provider?.name || '?';
  const initials = label.slice(0, 2).toUpperCase();
  const color = provider?.accentColor || '#64748b';

  if (logo && provider?.id && !broken) {
    return (
      <span
        className="provider-avatar provider-avatar--logo"
        // Height only, and more of it than a badge would take: a logo is not a
        // square. Constraining a wide mark to the badge's box leaves it fitted by
        // its longer side and reading much smaller than the two letters it
        // replaced, so the height drives and the width follows the asset's own
        // aspect ratio (see .provider-avatar--logo).
        style={{ height: size * LOGO_SCALE }}
        title={provider?.name}
      >
        <img
          src={`${process.env.PUBLIC_URL}/assets/${provider.id}.svg`}
          alt={provider?.name || provider.id}
          onError={() => setBroken(true)}
        />
      </span>
    );
  }

  return (
    <span
      className="provider-avatar"
      style={{
        width: size,
        height: size,
        background: `${color}22`,
        color,
        border: `1px solid ${color}55`,
        fontSize: size * 0.4,
      }}
      title={provider?.name}
    >
      {initials}
    </span>
  );
}
